import { useLoadingStore } from '/@/store/modules/loading';
import {
  composeFaviconWithBadge,
  DEFAULT_FAVICON_HREF,
  setFaviconHref,
} from './tab_task_badge_favicon';
import {
  planOnStatusEnter,
  planOnVisibilityChange,
  RESULT_BADGE_VISIBLE_MS,
  type DismissPlan,
  type TabTaskStatus,
  type Visibility,
} from './tab_task_badge_policy';

export interface ITabTaskBadgeDeps {
  getStatus: () => TabTaskStatus;
  subscribeStatus: (cb: () => void) => () => void;
  clearTaskStatus: () => void;
  getVisibility: () => Visibility;
  addVisibilityListener: (cb: () => void) => () => void;
  setFaviconHref: (href: string) => void;
  composeFavicon: (status: Exclude<TabTaskStatus, 'idle'>) => Promise<string>;
  originalHref: string;
  setTimeout: typeof setTimeout;
  clearTimeout: typeof clearTimeout;
}

export function startTabTaskBadge(deps: ITabTaskBadgeDeps): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let composeSeq = 0;
  const schedule = deps.setTimeout;
  const unschedule = deps.clearTimeout;

  const clearTimer = () => {
    if (timer !== undefined) {
      unschedule(timer);
      timer = undefined;
    }
  };

  const applyFavicon = async (status: TabTaskStatus) => {
    if (status === 'idle') {
      composeSeq += 1;
      deps.setFaviconHref(deps.originalHref);
      return;
    }
    const seq = ++composeSeq;
    try {
      const href = await deps.composeFavicon(status);
      if (seq !== composeSeq) {
        return;
      }
      deps.setFaviconHref(href);
    } catch (error) {
      console.warn('tab task badge compose failed', error);
    }
  };

  const applyPlan = (plan: DismissPlan, status: TabTaskStatus) => {
    clearTimer();
    if (plan === 'now') {
      deps.clearTaskStatus();
      return;
    }
    void applyFavicon(status);
    if (plan === 'after-visible-ms') {
      timer = schedule(() => {
        timer = undefined;
        deps.clearTaskStatus();
      }, RESULT_BADGE_VISIBLE_MS);
    }
  };

  const onStatus = () => {
    const status = deps.getStatus();
    applyPlan(planOnStatusEnter(status, deps.getVisibility()), status);
  };

  const onVisibility = () => {
    const status = deps.getStatus();
    const plan = planOnVisibilityChange(status, deps.getVisibility());
    if (plan === 'now') {
      clearTimer();
      deps.clearTaskStatus();
      return;
    }
    if (plan === 'on-focus') {
      clearTimer();
    }
  };

  const unsubStatus = deps.subscribeStatus(onStatus);
  const unsubVis = deps.addVisibilityListener(onVisibility);
  onStatus();

  return () => {
    clearTimer();
    unsubStatus();
    unsubVis();
  };
}

export function startAppTabTaskBadge(): () => void {
  const loadingStore = useLoadingStore();
  return startTabTaskBadge({
    getStatus: () => loadingStore.taskStatus,
    subscribeStatus: (cb) => {
      let prev = loadingStore.taskStatus;
      return loadingStore.$subscribe(() => {
        if (loadingStore.taskStatus === prev) {
          return;
        }
        prev = loadingStore.taskStatus;
        cb();
      });
    },
    clearTaskStatus: () => loadingStore.clearTaskStatus(),
    getVisibility: () => (document.visibilityState === 'hidden' ? 'hidden' : 'visible'),
    addVisibilityListener: (cb) => {
      document.addEventListener('visibilitychange', cb);
      return () => document.removeEventListener('visibilitychange', cb);
    },
    setFaviconHref,
    composeFavicon: composeFaviconWithBadge,
    originalHref: DEFAULT_FAVICON_HREF,
    setTimeout: window.setTimeout.bind(window),
    clearTimeout: window.clearTimeout.bind(window),
  });
}
